import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Newspaper } from "lucide-react";
import api, { imgSrc } from "../lib/api";
import { SectionHeading, stagger, fadeUp, Reveal } from "../lib/motion";
import { t } from "../i18n";

export const PostCard = ({ post }) => {
  const navigate = useNavigate();
  return (
    <motion.article
      variants={fadeUp}
      onClick={() => navigate(`/blog/${post.slug}`)}
      data-testid={`blog-card-${post.slug}`}
      className="group glass-card card-lift rounded-2xl overflow-hidden cursor-pointer flex flex-col"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={post.cover_image ? imgSrc(post.cover_image) : "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80"}
          alt={post.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <span className="absolute top-4 left-4 glass-dark border border-gold/30 rounded-full px-3.5 py-1.5 text-[11px] font-bold tracking-[0.15em] text-gold font-display uppercase">
          {post.category}
        </span>
      </div>
      <div className="p-7 flex flex-col flex-1">
        <h3 className="font-display text-lg font-bold text-white leading-snug mb-3 group-hover:text-gold transition-colors">
          {post.title}
        </h3>
        <p className="text-sm text-slate-400 leading-relaxed flex-1 line-clamp-3">{post.excerpt}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] text-gold font-display">
          {t("blog.read")} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </motion.article>
  );
};

const BlogPreview = () => {
  const [posts, setPosts] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  return (
    <section id="blog-preview" data-testid="blog-section" className="relative py-24 lg:py-32 bg-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("blog.eyebrow")}
          title={t("blog.title")}
          description={t("blog.desc")}
          testid="blog-heading"
        />
        {posts && posts.length > 0 ? (
          <>
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {posts.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </motion.div>
            <Reveal className="text-center mt-12">
              <button
                onClick={() => navigate("/blog")}
                data-testid="blog-view-all-btn"
                className="btn-outline-light rounded-full px-8 py-3.5 text-xs font-bold tracking-[0.2em] font-display inline-flex items-center gap-2"
              >
                {t("blog.viewAll")} <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </>
        ) : posts && posts.length === 0 ? (
          <Reveal className="max-w-2xl mx-auto">
            <div className="rounded-2xl border border-dashed border-gold/40 bg-gold/[0.03] p-12 text-center" data-testid="blog-placeholder">
              <Newspaper className="h-10 w-10 text-gold mx-auto mb-5" />
              <h3 className="font-display text-xl font-bold text-white mb-3">{t("blog.emptyTitle")}</h3>
              <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
                {t("blog.emptyText")}
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="h-40" />
        )}
      </div>
    </section>
  );
};

export default BlogPreview;
